// Gmail API 整合
// Supabase 已移除，改用 Cloud SQL
import { db } from './cloudsql';
import axios from 'axios';
import { getAuthHeaders } from './apiClient';

// API 基礎 URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://teleworker-api-5ubu67iiga-de.a.run.app';

// 郵件資料類型
interface EmailData {
  to: string | string[];
  subject: string;
  body: string;
  attachments?: Array<{ name: string; url: string }>;
  template_id?: string;
  cc?: string[];
}

interface QuoteEmailData {
  quoteData: {
    quote_number: string;
    quote_name: string;
    customer_name: string;
    total_amount?: number;
    valid_until: string;
  };
  recipientEmail: string;
  recipientName?: string;
  pdfUrl?: string;
  subject?: string;
  body?: string;
  ccEmails?: string[];
}

interface InvoiceEmailData {
  invoiceData: {
    invoice_number: string;
    customer_name?: string;
    total?: number;
    issue_date: string;
    due_date: string;
  };
  recipientEmail: string;
  recipientName?: string;
  pdfUrl?: string;
  customMessage?: string;
}

interface ServiceCompensationEmailData {
  compensationData: {
    statement_number: string;
    recipient_name: string;
    total_amount?: number;
    period_start: string;
    period_end: string;
  };
  recipientEmail: string;
  recipientName?: string;
  pdfUrl?: string;
  customMessage?: string;
}

interface EmailTemplate {
  id: string;
  name: string;
  type: 'quote' | 'invoice' | 'payment_request' | 'service_receipt' | 'collection' | 'customer_deposit';
  subject: string;
  body: string;
  variables: string[];
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// 通用郵件發送函數
export const sendEmail = async (emailData: EmailData): Promise<boolean> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(`${API_BASE_URL}/send-email`, emailData, { headers });
    return (response.data as { success: boolean }).success;
  } catch {
    return false;
  }
};

// 發送報價單郵件
export const sendQuoteEmail = async (quoteData: QuoteEmailData): Promise<boolean> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(`${API_BASE_URL}/send-quote-email`, quoteData, { headers });
    return (response.data as { success: boolean }).success;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'response' in error) {
      // Axios 錯誤
      const axiosError = error as { response?: { data?: { error?: string; message?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.error || axiosError.response?.data?.message || axiosError.message;
      throw new Error(errorMessage || '郵件發送失敗');
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('郵件發送失敗：未知錯誤');
  }
};

// 發送發票郵件
export const sendInvoiceEmail = async (invoiceData: InvoiceEmailData): Promise<boolean> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(`${API_BASE_URL}/send-invoice-email`, invoiceData, { headers });
    return (response.data as { success: boolean }).success;
  } catch {
    return false;
  }
};

// 勞務報酬單功能已移除
export const sendServiceCompensationEmail = async (_compensationData: ServiceCompensationEmailData): Promise<boolean> => {
  throw new Error('勞務報酬單功能已移除');
};

// 獲取郵件模板
// @param type - 可選的模板類型篩選器，例如 'quote' 只顯示報價單模板
export const getEmailTemplates = async (type?: string): Promise<EmailTemplate[]> => {
  try {
    const where: Record<string, unknown> = { is_active: true };
    
    // 如果指定了類型，則按類型篩選
    if (type) {
      where.type = type;
    }
    
    const data = await db.query<EmailTemplate>('email_templates', {
      where,
      orderBy: [{ field: 'sort_order', direction: 'asc' }]
    });
    
    return data || [];
  } catch {
    return [];
  }
};

// 創建郵件模板
export const createEmailTemplate = async (
  template: Omit<EmailTemplate, 'id' | 'created_at' | 'updated_at'>
): Promise<boolean> => {
  try {
    await db.create('email_templates', template);
    return true;
  } catch {
    return false;
  }
};

// 替換模板變數
export const replaceTemplateVariables = (template: string, variables: Record<string, unknown>): string => {
  let result = template;
  
  Object.entries(variables).forEach(([key, value]) => {
    const regex = new RegExp(`{{${key}}}`, 'g');
    const displayValue = value !== null && value !== undefined ? String(value) : '';
    result = result.replace(regex, displayValue);
  });
  
  return result;
};

// 更新郵件模板
export async function updateEmailTemplate(id: string, templateData: {
  name?: string;
  type?: 'quote' | 'invoice' | 'payment_request' | 'service_receipt' | 'collection' | 'customer_deposit';
  subject?: string;
  body?: string;
  variables?: string[];
  is_active?: boolean;
  sort_order?: number;
}) {
  try {
    // 檢查記錄是否存在
    const existing = await db.getById('email_templates', id);

    if (!existing) {
      // 記錄不存在，創建新記錄
      const newData = await db.create('email_templates', {
        id: id,
        ...templateData
      });
      return newData;
    }

    // 執行更新操作
    const updated = await db.update('email_templates', id, templateData);
    return updated;
  } catch (error) {
    throw new Error(`無法更新郵件模板: ${error instanceof Error ? error.message : '未知錯誤'}`);
  }
}

// 刪除郵件模板
export async function deleteEmailTemplate(id: string) {
  try {
    await db.delete('email_templates', id);
    return { success: true };
  } catch (error) {
    throw new Error(`無法刪除郵件模板: ${error instanceof Error ? error.message : '未知錯誤'}`);
  }
}

// 檢查 Gmail API 狀態
export async function checkGmailStatus() {
  try {
    const response = await fetch(`${API_BASE_URL}/health`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      // 添加超時設置
      signal: AbortSignal.timeout(5000) // 5秒超時
    });
    
    if (!response.ok) {
      throw new Error('API 服務不可用');
    }

    const data = await response.json();
    return {
      api_healthy: true,
      gmail_configured: data.gmail_configured || false
    };
  } catch {
    // 靜默處理錯誤，避免在控制台顯示太多錯誤
    return {
      api_healthy: false,
      gmail_configured: false
    };
  }
}

// 檢查用戶認證狀態（Supabase 已移除，改為簡單檢查）
export async function checkAuthStatus() {
  // 認證改由 Firebase 或其他方式處理
  return { 
    isAuthenticated: true, 
    user: null 
  };
}

 